/**
 * Client-side help search. The panel downloads one permission-filtered index
 * and matches it here: no per-keystroke requests, no AI, no external services.
 * Short tokens (AR, AP, JV, GRN) match exactly or through the synonym list.
 * Longer words also allow a small edit distance so a typo still hits.
 */

export interface TextRange {
    start: number;
    end: number;
}

export interface HelpArticleDoc {
    id: string;
    title: string;
    routes?: string[];
    query?: Record<string, string>;
    howtoTitle?: string;
    steps?: string[];
    tourTexts?: string[];
    searchText?: string[];
    hasTour?: boolean;
    keys?: string[];
    helpCenterKey?: string;
}

export interface HelpScreenDoc {
    title: string;
    url: string;
    fullPath: string;
}

export interface ArticleMatch {
    article: HelpArticleDoc;
    score: number;
    titleRanges: TextRange[];
    snippet: string;
    snippetRanges: TextRange[];
}

export interface ScreenMatch {
    screen: HelpScreenDoc;
    score: number;
    titleRanges: TextRange[];
    pathRanges: TextRange[];
}

interface Phrase {
    words: string[];
    canonical: string;
}

interface SearchField {
    text: string;
    weight: number;
    role: "title" | "snippet" | "hidden";
}

const MARK = "\uE000";

/** First id is the canonical token. Phrases are matched longest-first. */
const GROUPS: { id: string; terms: string[] }[] = [
    { id: "invoice", terms: ["invoice", "bill", "invois", "انوائس", "بل"] },
    { id: "customer", terms: ["customer", "client", "party", "گاہک", "gahak", "graahak"] },
    { id: "stock", terms: ["stock", "inventory", "stok", "اسٹاک", "maal"] },
    { id: "return", terms: ["return", "refund", "pos return", "till return", "واپسی", "wapsi"] },
    { id: "grn", terms: ["grn", "goods received", "goods receipt", "purchase receive", "مال وصول", "maal wasool"] },
    { id: "jv", terms: ["jv", "journal voucher", "جرنل", "jarnal"] },
    { id: "receivable", terms: ["receivable", "receivables", "accounts receivable", "ar", "وصولی", "wasooli"] },
    { id: "payable", terms: ["payable", "payables", "accounts payable", "ap", "ادائیگی", "adaigi"] },
    { id: "so", terms: ["so", "sales order"] },
    { id: "po", terms: ["po", "purchase order"] },
    { id: "cn", terms: ["cn", "credit note"] },
    { id: "dn", terms: ["dn", "debit note"] },
    { id: "rv", terms: ["rv", "receipt voucher"] },
    { id: "pv", terms: ["pv", "payment voucher"] },
    { id: "sj", terms: ["sj", "standing journal", "recurring journal", "recurring"] },
    { id: "bp", terms: ["bp", "business partner", "business partners"] },
    { id: "sku", terms: ["sku", "item code", "item master"] },
    { id: "baddebt", terms: ["bad debt", "write off", "writeoff"] },
    { id: "delivery", terms: ["sales delivery"] },
    { id: "currency", terms: ["currency", "currencies", "exchange rate", "fx", "multicurrency", "multi currency", "revaluation"] },
    { id: "packaging", terms: ["packaging", "package", "pack", "barcode"] },
    { id: "allocation", terms: ["allocation", "allocate", "settlement"] },
    { id: "promotion", terms: ["promotion", "promo", "discount"] },
    { id: "gl", terms: ["gl", "general ledger"] },
    { id: "coa", terms: ["coa", "chart of accounts"] },
    { id: "tb", terms: ["tb", "trial balance"] },
    { id: "pl", terms: ["pl", "p&l", "pnl", "profit and loss", "income statement"] },
    { id: "bs", terms: ["bs", "balance sheet"] },
    { id: "cashflow", terms: ["cash flow", "cashflow"] },
    { id: "acctstmt", terms: ["account statement", "gl journal detail"] },
    { id: "aging", terms: ["aging", "ageing"] },
    { id: "bankrec", terms: ["bank rec", "bank reconciliation"] },
    { id: "opening", terms: ["ob", "opening balance", "opening balances"] },
    { id: "uom", terms: ["uom", "unit of measure", "units of measure", "unit", "units"] },
    { id: "stocktake", terms: ["stock take", "stocktake", "stock count", "cycle count", "physical count", "stock adjustment"] },
    { id: "xfer", terms: ["stock transfer", "location transfer", "transit", "in transit", "cross branch"] },
    { id: "batch", terms: ["batch", "batches", "lot", "lots", "lot number", "lot no"] },
    { id: "serialno", terms: ["serial", "serials", "serial number", "serial numbers"] },
    { id: "bin", terms: ["bin", "warehouse location"] },
    { id: "whdash", terms: ["warehouse dashboard", "capacity", "low stock", "putaway", "pick suggestion", "replenishment"] },
    { id: "fifo", terms: ["fifo", "weighted average", "wac"] },
    { id: "quotation", terms: ["quotation", "quote", "sales quote"] },
    { id: "lead", terms: ["lead", "leads", "prospect"] },
    { id: "opportunity", terms: ["opportunity", "opportunities", "pipeline"] },
    { id: "payroll", terms: ["payroll", "payslip", "payslips"] },
    { id: "pricelist", terms: ["price list", "price group"] },
    { id: "bom", terms: ["bom", "bill of material", "bill of materials"] },
    { id: "workorder", terms: ["work order", "work orders"] },
    { id: "openshift", terms: ["open shift"] },
    { id: "helpcenter", terms: ["help center"] }
];

const PHRASES: Phrase[] = [];
const SURFACES = new Map<string, string[]>();

for (const group of GROUPS) {
    const canonical = MARK + group.id;
    const surfaces = new Set<string>();
    for (const term of group.terms) {
        const words = foldWords(term);
        if (!words.length)
            continue;
        PHRASES.push({ words, canonical });
        for (const word of words) {
            if ([...word].length >= 5)
                surfaces.add(word);
        }
    }
    SURFACES.set(canonical, [...surfaces]);
}

PHRASES.sort((a, b) => b.words.length - a.words.length || b.words.join(" ").length - a.words.join(" ").length);

export function searchHelp(
    query: string,
    articles: HelpArticleDoc[] | null | undefined,
    screens: HelpScreenDoc[] | null | undefined,
    limit = 8
): { articles: ArticleMatch[]; screens: ScreenMatch[] } {
    const queryTokens = unique(canonicalize(foldWords(query || "")));
    if (!queryTokens.length)
        return { articles: [], screens: [] };

    const articleHits: ArticleMatch[] = [];
    for (const article of articles || []) {
        if (!article)
            continue;
        const ranked = rankFields(articleFields(article), queryTokens);
        if (!ranked)
            continue;
        articleHits.push({
            article,
            score: ranked.score,
            titleRanges: ranked.titleRanges,
            snippet: ranked.snippet,
            snippetRanges: ranked.snippetRanges
        });
    }

    const screenHits: ScreenMatch[] = [];
    for (const screen of screens || []) {
        if (!screen?.url)
            continue;
        const title = cover(screen.title || "", queryTokens);
        const path = cover(screen.fullPath || "", queryTokens);
        const matched = mergeCoverage(title.matched, path.matched);
        if (matched.size < queryTokens.length)
            continue;
        let score = 0;
        for (const quality of matched.values())
            score += quality;
        if (title.matched.size === queryTokens.length)
            score += 20;
        screenHits.push({
            screen,
            score,
            titleRanges: title.ranges,
            pathRanges: path.ranges
        });
    }

    articleHits.sort(byScore);
    screenHits.sort(byScore);
    return {
        articles: articleHits.slice(0, limit),
        screens: screenHits.slice(0, limit)
    };
}

/**
 * True when a navigation URL is the same screen as a help article.
 * Query constraints on the article are required; extra parameters are ignored.
 */
export function screenUrlMatches(
    url: string,
    routes: string[] | null | undefined,
    query?: Record<string, string> | null,
    root = "/"
): boolean {
    if (!routes?.length || !url)
        return false;
    const path = normalizePath(splitUrl(url).path, root);
    if (!routes.some(route => normalizePath(route, root) === path))
        return false;
    if (!query)
        return true;
    const actual = queryMap(splitUrl(url).query);
    for (const [key, value] of Object.entries(query)) {
        if ((actual.get(key.toLowerCase()) || "") !== String(value).toLowerCase())
            return false;
    }
    return true;
}

/** Path plus query, without helpTour, so two links to the same screen compare equal. */
export function canonicalScreenUrl(url: string, root = "/"): string {
    const parts = splitUrl(url || "");
    const path = normalizePath(parts.path, root);
    const map = queryMap(parts.query);
    const keys = [...map.keys()].sort();
    const query = keys.map(key => key + "=" + map.get(key)).join("&");
    return path + (query ? "?" + query : "");
}

function articleFields(article: HelpArticleDoc): SearchField[] {
    const fields: SearchField[] = [
        { text: article.title || "", weight: 4, role: "title" }
    ];
    if (article.keys?.length)
        fields.push({ text: article.keys.join(" "), weight: 2, role: "hidden" });
    if (article.howtoTitle)
        fields.push({ text: article.howtoTitle, weight: 2, role: "snippet" });
    for (const step of article.steps || [])
        fields.push({ text: step, weight: 1, role: "snippet" });
    for (const step of article.tourTexts || [])
        fields.push({ text: step, weight: 1, role: "snippet" });
    for (const blob of article.searchText || [])
        fields.push({ text: blob, weight: 1, role: "snippet" });
    return fields;
}

function rankFields(fields: SearchField[], queryTokens: string[]) {
    const best = new Map<number, number>();
    let titleRanges: TextRange[] = [];
    let titleMatched = 0;
    let snippet = "";
    let snippetRanges: TextRange[] = [];
    let snippetTokens = -1;
    let snippetQuality = -1;
    let sawTitle = false;

    for (const field of fields) {
        if (!field.text)
            continue;
        const covered = cover(field.text, queryTokens);
        if (field.role === "title" && !sawTitle) {
            titleRanges = covered.ranges;
            titleMatched = covered.matched.size;
            sawTitle = true;
        }
        let qualitySum = 0;
        for (const [index, quality] of covered.matched) {
            qualitySum += quality;
            const weighted = quality * field.weight;
            if (weighted > (best.get(index) || 0))
                best.set(index, weighted);
        }
        if (field.role === "snippet" && covered.matched.size > 0 &&
            (covered.matched.size > snippetTokens ||
                (covered.matched.size === snippetTokens && qualitySum > snippetQuality))) {
            snippet = field.text;
            snippetRanges = covered.ranges;
            snippetTokens = covered.matched.size;
            snippetQuality = qualitySum;
        }
    }

    if (best.size < queryTokens.length)
        return null;
    if (!snippet) {
        const fallback = fields.find(field => field.role === "snippet" && field.text);
        snippet = fallback?.text || "";
        snippetRanges = [];
    }
    let score = 0;
    for (const value of best.values())
        score += value;
    if (titleMatched === queryTokens.length)
        score += 20;
    return { score, titleRanges, snippet, snippetRanges };
}

function cover(text: string, queryTokens: string[]) {
    const spans = wordSpans(text);
    const words = spans.map(span => span.fold);
    const matched = new Map<number, number>();
    const ranges: TextRange[] = [];
    for (let i = 0; i < spans.length;) {
        // A longer synonym can start with a shorter one ("GL Journal Detail"
        // is both an account statement and a GL screen). Keep every phrase
        // that starts here, and highlight only the one the query matched.
        const phrases = findPhrases(words, i);
        const count = phrases.length ? phrases[0].words.length : 1;
        const tokens = phrases.length
            ? phrases.map(phrase => ({ token: phrase.canonical, count: phrase.words.length }))
            : [{ token: words[i], count: 1 }];
        let highlight = 0;
        queryTokens.forEach((queryToken, index) => {
            let best = 0;
            let bestCount = 0;
            for (const candidate of tokens) {
                const quality = scorePair(queryToken, candidate.token);
                if (quality <= best)
                    continue;
                best = quality;
                bestCount = candidate.count;
            }
            if (best <= 0)
                return;
            highlight = Math.max(highlight, bestCount);
            if (best > (matched.get(index) || 0))
                matched.set(index, best);
        });
        if (highlight > 0) {
            for (let k = 0; k < highlight; k++)
                ranges.push({ start: spans[i + k].start, end: spans[i + k].end });
        }
        i += count;
    }
    return { matched, ranges: mergeRanges(ranges) };
}

function mergeCoverage(left: Map<number, number>, right: Map<number, number>) {
    const merged = new Map<number, number>();
    for (const [index, quality] of left)
        merged.set(index, quality * 4);
    for (const [index, quality] of right) {
        const weighted = quality * 3;
        if (weighted > (merged.get(index) || 0))
            merged.set(index, weighted);
    }
    return merged;
}

function scorePair(queryToken: string, docToken: string): number {
    if (queryToken === docToken)
        return 10;
    const queryCanon = queryToken.startsWith(MARK);
    const docCanon = docToken.startsWith(MARK);
    if (queryCanon)
        return 0;
    if (docCanon) {
        let best = 0;
        for (const surface of SURFACES.get(docToken) || [])
            best = Math.max(best, fuzzy(queryToken, surface));
        return best;
    }
    return fuzzy(queryToken, docToken);
}

function fuzzy(query: string, doc: string): number {
    if (query === doc)
        return 9;
    const q = [...query];
    const d = [...doc];
    if (q.length <= 3 || d.length <= 3 || q[0] !== d[0])
        return 0;
    if (q.length >= 4 && doc.startsWith(query) && d.length - q.length > 0 && d.length - q.length <= 3)
        return 7;
    const limit = q.length <= 4 ? 1 : 2;
    if (Math.abs(q.length - d.length) > limit)
        return 0;
    const distance = damerau(q, d, limit);
    if (distance <= 0 || distance > limit)
        return 0;
    return distance === 1 ? 8 : 6;
}

/** Damerau-Levenshtein. Adjacent transpositions count as one edit. Stops past max. */
function damerau(a: string[], b: string[], max: number): number {
    const n = a.length;
    const m = b.length;
    const rowCount = n + 1;
    const d: number[] = new Array(rowCount * (m + 1));
    const at = (i: number, j: number) => i * (m + 1) + j;
    for (let i = 0; i <= n; i++)
        d[at(i, 0)] = i;
    for (let j = 0; j <= m; j++)
        d[at(0, j)] = j;
    for (let i = 1; i <= n; i++) {
        let rowMin = max + 1;
        for (let j = 1; j <= m; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            let value = Math.min(
                d[at(i - 1, j)] + 1,
                d[at(i, j - 1)] + 1,
                d[at(i - 1, j - 1)] + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
                value = Math.min(value, d[at(i - 2, j - 2)] + 1);
            d[at(i, j)] = value;
            if (value < rowMin)
                rowMin = value;
        }
        if (rowMin > max)
            return max + 1;
    }
    return d[at(n, m)];
}

function findPhrase(words: string[], index: number): Phrase | null {
    const found = findPhrases(words, index);
    return found.length ? found[0] : null;
}

/** Every synonym phrase that starts here, longest first. */
function findPhrases(words: string[], index: number): Phrase[] {
    const found: Phrase[] = [];
    for (const phrase of PHRASES) {
        const count = phrase.words.length;
        if (index + count > words.length)
            continue;
        let matches = true;
        for (let k = 0; k < count; k++) {
            if (!sameWord(words[index + k], phrase.words[k], k === count - 1)) {
                matches = false;
                break;
            }
        }
        if (matches)
            found.push(phrase);
    }
    return found;
}

function sameWord(actual: string, expected: string, allowPlural: boolean): boolean {
    if (actual === expected)
        return true;
    if (!allowPlural || !/^[a-z]+$/.test(expected) || expected.length < 4)
        return false;
    return actual === expected + "s" || actual === expected + "es";
}

function canonicalize(words: string[]): string[] {
    const tokens: string[] = [];
    for (let i = 0; i < words.length;) {
        const phrase = findPhrase(words, i);
        if (phrase) {
            tokens.push(phrase.canonical);
            i += phrase.words.length;
        }
        else {
            tokens.push(words[i]);
            i++;
        }
    }
    return tokens;
}

function unique(tokens: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const token of tokens) {
        if (seen.has(token))
            continue;
        seen.add(token);
        result.push(token);
    }
    return result;
}

function foldWords(text: string): string[] {
    return wordSpans(text).map(span => span.fold);
}

function wordSpans(text: string): { fold: string; start: number; end: number }[] {
    const spans: { fold: string; start: number; end: number }[] = [];
    for (const match of (text || "").matchAll(/[\p{L}\p{N}]+/gu)) {
        const raw = match[0];
        const start = match.index || 0;
        spans.push({ fold: raw.toLocaleLowerCase("en"), start, end: start + raw.length });
    }
    return spans;
}

function mergeRanges(ranges: TextRange[]): TextRange[] {
    const sorted = ranges
        .filter(range => range.end > range.start)
        .sort((a, b) => a.start - b.start || a.end - b.end);
    const merged: TextRange[] = [];
    for (const range of sorted) {
        const last = merged[merged.length - 1];
        if (last && range.start <= last.end)
            last.end = Math.max(last.end, range.end);
        else
            merged.push({ start: range.start, end: range.end });
    }
    return merged;
}

function splitUrl(url: string): { path: string; query: URLSearchParams } {
    const hashless = url.split("#")[0];
    const question = hashless.indexOf("?");
    const path = question >= 0 ? hashless.slice(0, question) : hashless;
    const query = new URLSearchParams(question >= 0 ? hashless.slice(question + 1) : "");
    return { path, query };
}

function queryMap(params: URLSearchParams): Map<string, string> {
    const map = new Map<string, string>();
    params.forEach((value, key) => {
        const name = key.toLowerCase();
        if (name === "helptour" || name === "help")
            return;
        map.set(name, value.toLowerCase());
    });
    return map;
}

function normalizePath(path: string, root: string): string {
    let value = (path || "").trim().replace(/\\/g, "/");
    if (value.startsWith("~/"))
        value = "/" + value.slice(2);
    else if (value.startsWith("~"))
        value = "/" + value.slice(1);
    let prefix = root || "/";
    if (!prefix.startsWith("/"))
        prefix = "/" + prefix;
    if (prefix.length > 1)
        prefix = prefix.replace(/\/+$/, "");
    if (prefix.length > 1 && value.toLowerCase().startsWith(prefix.toLowerCase()))
        value = value.slice(prefix.length) || "/";
    if (!value.startsWith("/"))
        value = "/" + value;
    if (value.length > 1)
        value = value.replace(/\/+$/, "");
    return value.toLowerCase();
}

function byScore<T extends { score: number }>(a: T, b: T): number {
    return b.score - a.score;
}
